import { useEffect, useRef, useState } from 'react';
import { Instagram, Play } from 'lucide-react';
import { SITE } from '@/data';
import { Reveal, useEmbedScript } from './Reveal';
import { TikTokIcon } from './Mark';

const fmt = (n) => {
  const v = Number(n) || 0;
  if (v >= 1e6) return (v / 1e6).toFixed(v >= 1e7 ? 0 : 1).replace(/\.0$/, '') + 'M';
  if (v >= 1e3) return (v / 1e3).toFixed(v >= 1e5 ? 0 : 1).replace(/\.0$/, '') + 'K';
  return String(v);
};

const STAT_ROWS = [
  { key: 'views', l: 'VIEWS' },
  { key: 'likes', l: 'LIKES' },
  { key: 'shares', l: 'SHARES' },
  { key: 'saves', l: 'SAVES' },
  { key: 'comments', l: 'COMMENTS' }
];

/* Real TikTok embeds — the actual videos, playable in place.
   If the embed script is blocked (ad-blockers etc.), a styled tap-through card
   takes over after 5s so visitors never see raw text. */
function TikTokEmbed({ url, index }) {
  useEmbedScript('https://www.tiktok.com/embed.js', '__ifbTikTokJs');
  const [failed, setFailed] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => {
      if (ref.current && !ref.current.querySelector('iframe')) setFailed(true);
    }, 5000);
    return () => clearTimeout(t);
  }, []);

  const m = url.match(/video\/(\d+)/);
  const videoId = m ? m[1] : '';

  if (failed) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`viral-tiktok-fallback-${index + 1}`}
        className="group relative block w-full max-w-[300px] overflow-hidden rounded-[28px] border border-ivory/15 bg-gradient-to-b from-plum-card2 to-plum-deep shadow-[0_24px_80px_rgba(0,0,0,0.45)] transition-transform duration-300 hover:-translate-y-1.5"
      >
        <div className="flex aspect-[9/16] flex-col items-center justify-center gap-5 p-6 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-gold text-plum transition-transform duration-300 group-hover:scale-110">
            <Play size={24} fill="currentColor" aria-hidden="true" />
          </span>
          <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-ivory/60">Tap to watch on TikTok</span>
        </div>
      </a>
    );
  }

  return (
    <div ref={ref} className="flex justify-center [&_iframe]:!max-w-full" data-testid={`viral-tiktok-embed-${index + 1}`}>
      <blockquote
        className="tiktok-embed"
        cite={url}
        data-video-id={videoId}
        style={{ margin: 0, maxWidth: 'none', minWidth: 0 }}
      >
        <section>
          <a target="_blank" rel="noopener noreferrer" href={url}>
            <strong>Watch on TikTok</strong>
          </a>
        </section>
      </blockquote>
    </div>
  );
}

export default function Viral() {
  const reels = SITE.viralReels.filter(Boolean);
  const featured = reels[SITE.viralFeaturedIndex] || reels[0];
  const rest = reels.filter((r) => r !== featured);
  const [stats, setStats] = useState(SITE.viralStats);
  const [live, setLive] = useState(false);

  useEffect(() => {
    const m = (featured || '').match(/video\/(\d+)/);
    if (!m) return;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/viral/stats`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(({ videos }) => {
        const v = (videos || []).find((x) => x.videoId === m[1]);
        if (v) {
          setStats(STAT_ROWS.map(({ key, l }) => ({ n: fmt(v[key]), l })));
          setLive(true);
        }
      })
      .catch(() => {});
  }, [featured]);
  return (
    <section id="viral" className="mx-auto max-w-[1280px] px-6 pt-24 md:pt-32" data-testid="viral-section">
      <Reveal>
        <div className="mb-12 flex flex-wrap items-end justify-between gap-6">
          <div>
            <span className="font-mono text-[13px] font-bold uppercase tracking-[0.25em] text-gold">Everyone's talking about</span>
            <h2 className="mt-3 font-display text-5xl uppercase leading-[0.95] text-ivory md:text-7xl">
              THE LATEST <span className="text-mauve">VIRAL</span> <span className="text-stroke-ivory">MOMENTS</span>
            </h2>
            <p className="mt-4 max-w-[460px] text-lg text-ivory/70">{SITE.viralLine}</p>
          </div>
          <a
            href="https://www.tiktok.com/@princessnyahandstuff"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="viral-tiktok-link"
            className="inline-flex items-center gap-2.5 rounded-full border-2 border-ivory/20 px-6 py-3.5 font-bold text-ivory transition-[border-color,color,transform] duration-200 hover:scale-[1.03] hover:border-mauve hover:text-mauve"
          >
            <TikTokIcon size={18} />
            Follow @princessnyahandstuff
          </a>
        </div>
      </Reveal>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12 lg:items-stretch lg:gap-8">
        <Reveal className="lg:col-span-5">
          <div className="flex h-full items-center justify-center rounded-[28px] border border-ivory/15 bg-plum-card p-4 shadow-[0_24px_80px_rgba(0,0,0,0.45)]" data-testid="viral-featured">
            <TikTokEmbed url={featured} index={0} />
          </div>
        </Reveal>

        <Reveal delay={0.1} className="lg:col-span-7">
          <div className="flex h-full flex-col justify-center rounded-[28px] border border-ivory/10 bg-plum-card/50 p-8 md:p-10" data-testid="viral-stats">
            <span className="mb-2 font-mono text-[13px] font-bold uppercase tracking-[0.25em] text-mauve">
              One clip. The numbers.{live && <span className="ml-3 text-sky">live · updated daily</span>}
            </span>
            {stats.map((s) => (
              <div key={s.l} className="flex items-baseline justify-between gap-6 border-b border-ivory/10 py-4 last:border-b-0 md:py-5">
                <span className="font-display text-5xl leading-none text-gold md:text-7xl" data-testid={`viral-stat-${s.l.toLowerCase()}`}>{s.n}</span>
                <span className="font-mono text-sm font-bold tracking-[0.2em] text-ivory/60 md:text-base">{s.l}</span>
              </div>
            ))}
            <span className="mt-3 font-mono text-[11px] uppercase tracking-[0.2em] text-ivory/40">…and still climbing</span>
          </div>
        </Reveal>
      </div>

      {rest.length > 0 && (
        <div className="mx-auto mt-6 grid max-w-[780px] grid-cols-1 justify-items-center gap-6 sm:grid-cols-2">
          {rest.map((r, i) => (
            <TikTokEmbed key={r} url={r} index={i + 1} />
          ))}
        </div>
      )}
    </section>
  );
}

